import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext, API, getAuthHeaders } from '../App';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Flag, Trophy, Save, X, Loader2, Camera, Check, Users
} from 'lucide-react';
import { toast } from 'sonner';
import ScorecardScanner from '../components/ScorecardScanner';

export default function LiveScorecard() {
  const { liveRoundId } = useParams();
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [round, setRound] = useState(null);
  const [loadingRound, setLoadingRound] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  // Editing state: which cell is active
  const [editCell, setEditCell] = useState(null); // {playerIdx, holeIdx}
  const [editValue, setEditValue] = useState('');
  const editRef = useRef(null);
  // Par editing state
  const [editParHole, setEditParHole] = useState(null); // holeIdx
  const [editParValue, setEditParValue] = useState('');
  const editParRef = useRef(null);

  useEffect(() => { if (!loading && !user) navigate('/'); }, [loading, user, navigate]);

  const fetchRound = useCallback(async () => {
    try {
      const res = await fetch(`${API}/live-rounds/${liveRoundId}`, { credentials: 'include', headers: getAuthHeaders() });
      if (res.ok) {
        setRound(await res.json());
      } else {
        toast.error('Round not found');
        navigate('/dashboard');
      }
    } catch { toast.error('Failed to load round'); }
    finally { setLoadingRound(false); }
  }, [liveRoundId, navigate]);

  useEffect(() => { if (user) fetchRound(); }, [user, fetchRound]);

  // Focus the input when a cell is tapped
  useEffect(() => {
    if (editCell && editRef.current) editRef.current.focus();
  }, [editCell]);

  useEffect(() => {
    if (editParHole != null && editParRef.current) editParRef.current.focus();
  }, [editParHole]);

  const players = round?.players?.length > 0 ? round.players : round ? [{ name: round.user_name, holes: round.holes, total_score: round.total_score, score_vs_par: round.score_vs_par }] : [];
  const numHoles = round?.num_holes || 18;
  const isOwner = round?.user_id === user?.user_id;
  const isCompleted = round?.status === 'completed';

  // Get par for a hole
  const getPar = (holeIdx) => round?.holes?.[holeIdx]?.par || 4;

  // Get yardage for a hole (0 if unknown)
  const getYardage = (holeIdx) => round?.holes?.[holeIdx]?.yardage || 0;

  // Get score for a player at a hole
  const getScore = (playerIdx, holeIdx) => {
    const p = players[playerIdx];
    if (!p) return null;
    return p.holes?.[holeIdx]?.score ?? null;
  };

  // Calculate front/back/total for a player
  const calcTotals = (playerIdx) => {
    const p = players[playerIdx];
    if (!p) return { out: 0, inn: 0, total: 0 };
    let out = 0, inn = 0;
    for (let h = 0; h < Math.min(9, numHoles); h++) {
      const s = p.holes?.[h]?.score;
      if (s != null) out += s;
    }
    for (let h = 9; h < numHoles; h++) {
      const s = p.holes?.[h]?.score;
      if (s != null) inn += s;
    }
    return { out, inn, total: out + inn };
  };

  // Color code a score cell based on par
  const cellColor = (score, par) => {
    if (score == null) return '';
    const diff = score - par;
    if (diff <= -2) return 'bg-yellow-400 text-yellow-900 font-black'; // Eagle
    if (diff === -1) return 'bg-red-500 text-white font-bold'; // Birdie
    if (diff === 0) return 'bg-bundle-green text-white font-bold'; // Par
    if (diff === 1) return 'bg-blue-100 text-blue-800 font-bold'; // Bogey
    return 'bg-gray-200 text-gray-700 font-bold'; // Double+
  };

  // Save a single score
  const saveScore = async (playerIdx, holeIdx, score) => {
    if (score === '' || score == null) return;
    const par = getPar(holeIdx);
    setSaving(true);
    try {
      const res = await fetch(`${API}/live-rounds/${liveRoundId}/hole`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          hole: holeIdx + 1,
          par,
          score: parseInt(score),
          putts: null,
          fairway_hit: null,
          gir: null,
          shots: [],
          player_index: playerIdx,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        setRound(prev => {
          const updated = { ...prev, ...result };
          if (result.players) updated.players = result.players;
          return updated;
        });
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Network error'); }
    finally { setSaving(false); }
  };

  const handleCellTap = (playerIdx, holeIdx) => {
    if (!isOwner || isCompleted) return;
    const current = getScore(playerIdx, holeIdx);
    setEditCell({ playerIdx, holeIdx });
    setEditValue(current != null ? String(current) : '');
  };

  const handleCellSave = () => {
    if (!editCell) return;
    const val = editValue.trim();
    if (val && !isNaN(val)) {
      saveScore(editCell.playerIdx, editCell.holeIdx, parseInt(val));
    }
    setEditCell(null);
    setEditValue('');
  };

  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCellSave();
      // Auto-advance to next hole
      if (editCell) {
        const nextHole = editCell.holeIdx + 1;
        if (nextHole < numHoles) {
          setTimeout(() => handleCellTap(editCell.playerIdx, nextHole), 100);
        }
      }
    }
  };

  // Update par for a hole across all players
  const saveHolePar = async (holeIdx, newPar) => {
    const par = parseInt(newPar);
    if (!par || par < 3 || par > 6) { toast.error('Par must be 3, 4, 5, or 6'); return; }
    if (par === getPar(holeIdx)) return; // no change
    try {
      const res = await fetch(`${API}/live-rounds/${liveRoundId}/hole-par`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ hole: holeIdx + 1, par }),
      });
      if (res.ok) {
        // Refetch the round to get updated totals
        await fetchRound();
        toast.success(`Hole ${holeIdx + 1} par updated to ${par}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update par');
      }
    } catch { toast.error('Network error'); }
  };

  const handleParTap = (holeIdx) => {
    if (!isOwner || isCompleted) return;
    setEditParHole(holeIdx);
    setEditParValue(String(getPar(holeIdx)));
  };

  const handleParSave = () => {
    if (editParHole == null) return;
    const val = editParValue.trim();
    if (val && !isNaN(val)) {
      saveHolePar(editParHole, val);
    }
    setEditParHole(null);
    setEditParValue('');
  };

  const handleParKeyDown = (e) => {
    if (e.key === 'Enter') handleParSave();
    else if (e.key === 'Escape') { setEditParHole(null); setEditParValue(''); }
  };

  const completeRound = async () => {
    if (!window.confirm('Complete this round?')) return;
    setCompleting(true);
    try {
      const res = await fetch(`${API}/live-rounds/${liveRoundId}/complete`, { method: 'PUT', credentials: 'include', headers: getAuthHeaders() });
      if (res.ok) { const data = await res.json(); setSummary(data); setShowSummary(true); toast.success('Round completed!'); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to complete'); }
    } catch { toast.error('Network error'); }
    finally { setCompleting(false); }
  };

  const abandonRound = async () => {
    if (!window.confirm('Abandon this round? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/live-rounds/${liveRoundId}`, { method: 'DELETE', credentials: 'include', headers: getAuthHeaders() });
      if (res.ok) { toast.success('Round abandoned'); navigate('/dashboard'); }
    } catch { toast.error('Network error'); }
  };

  if (loading || loadingRound || !round) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <Loader2 className="w-8 h-8 animate-spin text-bundle-green" />
      </div>
    );
  }

  // Par totals
  const parOut = Array.from({ length: Math.min(9, numHoles) }, (_, i) => getPar(i)).reduce((a, b) => a + b, 0);
  const parIn = numHoles > 9 ? Array.from({ length: numHoles - 9 }, (_, i) => getPar(i + 9)).reduce((a, b) => a + b, 0) : 0;
  const parTotal = parOut + parIn;

  // Render a 9-hole grid section
  const renderNine = (startHole, endHole, label) => {
    const hasYardage = Array.from({ length: endHole - startHole }, (_, i) => getYardage(startHole + i)).some(y => y > 0);
    return (
    <div className="overflow-x-auto mb-4">
      <table className="w-full border-collapse text-center text-xs" data-testid={`scorecard-${label}`}>
        <thead>
          {/* Hole numbers */}
          <tr className="bg-bundle-green text-white">
            <th className="px-2 py-2 text-left font-bold text-[11px] sticky left-0 bg-bundle-green z-10 min-w-[70px]">Hole</th>
            {Array.from({ length: endHole - startHole }, (_, i) => (
              <th key={i} className="px-1 py-2 font-bold min-w-[32px]">{startHole + i + 1}</th>
            ))}
            <th className="px-2 py-2 font-bold bg-bundle-green-dark min-w-[38px]">{label}</th>
          </tr>
          {/* Yardage row (only shown when we have real yardage data) */}
          {hasYardage && (
            <tr className="bg-bundle-green/10 text-gray-700" data-testid={`yardage-row-${label}`}>
              <td className="px-2 py-1 text-left font-semibold text-[10px] sticky left-0 bg-[#e8f0e8] z-10">Yards</td>
              {Array.from({ length: endHole - startHole }, (_, i) => {
                const y = getYardage(startHole + i);
                return (
                  <td key={i} className="px-1 py-1 text-[10px] text-gray-600">{y > 0 ? y : '—'}</td>
                );
              })}
              <td className="px-2 py-1 text-[10px] font-semibold bg-gray-100 text-gray-700">
                {Array.from({ length: endHole - startHole }, (_, i) => getYardage(startHole + i)).reduce((a, b) => a + b, 0) || '—'}
              </td>
            </tr>
          )}
          {/* Par row — tap to edit */}
          <tr className="bg-bundle-cream-dark text-gray-600">
            <td className="px-2 py-1.5 text-left font-bold text-[11px] sticky left-0 bg-bundle-cream-dark z-10">
              Par {isOwner && !isCompleted && <span className="text-[9px] text-gray-400 font-normal">(tap to edit)</span>}
            </td>
            {Array.from({ length: endHole - startHole }, (_, i) => {
              const holeIdx = startHole + i;
              const isEditingPar = editParHole === holeIdx;
              const canEditPar = isOwner && !isCompleted;
              return (
                <td
                  key={i}
                  onClick={() => canEditPar && handleParTap(holeIdx)}
                  className={`px-1 py-1.5 font-medium ${canEditPar ? 'cursor-pointer hover:bg-bundle-green/10' : ''} ${isEditingPar ? 'ring-2 ring-bundle-green' : ''}`}
                  data-testid={`par-cell-${holeIdx}`}
                >
                  {isEditingPar ? (
                    <input
                      ref={editParRef}
                      type="number"
                      min="3" max="6"
                      value={editParValue}
                      onChange={e => setEditParValue(e.target.value)}
                      onBlur={handleParSave}
                      onKeyDown={handleParKeyDown}
                      className="w-8 text-center text-sm font-bold bg-white border-0 outline-none"
                    />
                  ) : getPar(holeIdx)}
                </td>
              );
            })}
            <td className="px-2 py-1.5 font-bold bg-gray-200">
              {Array.from({ length: endHole - startHole }, (_, i) => getPar(startHole + i)).reduce((a, b) => a + b, 0)}
            </td>
          </tr>
        </thead>
        <tbody>
          {players.map((player, pIdx) => {
            const nineTotal = Array.from({ length: endHole - startHole }, (_, i) => {
              const s = getScore(pIdx, startHole + i);
              return s != null ? s : 0;
            }).reduce((a, b) => a + b, 0);

            return (
              <tr key={pIdx} className={`border-b border-bundle-cream-dark ${pIdx % 2 === 0 ? 'bg-white' : 'bg-bundle-cream/50'}`}>
                <td className="px-2 py-2 text-left font-bold text-bundle-green text-[11px] sticky left-0 z-10 truncate max-w-[70px]"
                  style={{ backgroundColor: pIdx % 2 === 0 ? 'white' : '#f5f0e8' }}>
                  {player.name?.split(' ')[0] || `P${pIdx + 1}`}
                </td>
                {Array.from({ length: endHole - startHole }, (_, i) => {
                  const holeIdx = startHole + i;
                  const score = getScore(pIdx, holeIdx);
                  const par = getPar(holeIdx);
                  const isEditing = editCell?.playerIdx === pIdx && editCell?.holeIdx === holeIdx;

                  return (
                    <td key={i}
                      onClick={() => handleCellTap(pIdx, holeIdx)}
                      className={`px-0 py-0 cursor-pointer transition-all min-w-[32px] ${
                        isEditing ? 'ring-2 ring-bundle-green' : ''
                      }`}
                      data-testid={`cell-${pIdx}-${holeIdx}`}
                    >
                      {isEditing ? (
                        <input
                          ref={editRef}
                          type="number"
                          min="1" max="15"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onBlur={handleCellSave}
                          onKeyDown={handleCellKeyDown}
                          className="w-full h-8 text-center text-sm font-bold bg-white border-0 outline-none"
                        />
                      ) : (
                        <div className={`h-8 flex items-center justify-center text-sm ${
                          score != null ? cellColor(score, par) : 'text-gray-300'
                        }`}>
                          {score != null ? score : '-'}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-2 font-bold text-sm bg-gray-100 text-bundle-green">
                  {nineTotal || '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    );
  };

  // Summary view
  if (showSummary && summary) {
    return (
      <div className="min-h-screen bg-bundle-cream pb-mobile-nav">
        <Navigation user={user} />
        <div className="max-w-lg mx-auto px-4 pt-20">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-bundle-green/10 flex items-center justify-center">
              <Trophy className="w-8 h-8 text-bundle-gold" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-bundle-green">Round Complete!</h1>
            <p className="text-gray-500 text-sm">{round.course_name}</p>
          </div>
          <div className="bg-white rounded-2xl border border-bundle-cream-dark p-5 shadow-sm mb-4">
            <div className="grid grid-cols-3 gap-4 text-center mb-4">
              <div><p className="text-2xl font-bold text-bundle-green">{summary.total_score}</p><p className="text-[10px] text-gray-400">Score</p></div>
              <div><p className="text-2xl font-bold text-bundle-gold">{summary.par_text || (summary.score_vs_par > 0 ? '+' + summary.score_vs_par : summary.score_vs_par)}</p><p className="text-[10px] text-gray-400">vs Par</p></div>
              <div><p className="text-2xl font-bold text-gray-600">{summary.handicap_differential?.toFixed(1) || '--'}</p><p className="text-[10px] text-gray-400">HCP Diff</p></div>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {summary.stats?.eagles > 0 && <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold">{summary.stats.eagles} Eagle{summary.stats.eagles > 1 ? 's' : ''}</span>}
              {summary.stats?.birdies > 0 && <span className="px-3 py-1 rounded-full bg-red-100 text-red-600 text-xs font-bold">{summary.stats.birdies} Birdie{summary.stats.birdies > 1 ? 's' : ''}</span>}
              {summary.stats?.pars > 0 && <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">{summary.stats.pars} Par{summary.stats.pars > 1 ? 's' : ''}</span>}
              {summary.stats?.bogeys > 0 && <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">{summary.stats.bogeys} Bogey{summary.stats.bogeys > 1 ? 's' : ''}</span>}
              {summary.stats?.double_bogeys > 0 && <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-bold">{summary.stats.double_bogeys} Double+</span>}
            </div>
            {summary.is_new_pr && <p className="text-center mt-3 text-bundle-gold font-bold text-sm">New Personal Record!</p>}
          </div>
          {summary.additional_handicaps?.length > 0 && (
            <div className="bg-white rounded-2xl border border-bundle-cream-dark p-4 shadow-sm mb-4">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">Handicap Updates</p>
              {summary.additional_handicaps.map((h, i) => (
                <div key={i} className="flex justify-between text-sm py-1">
                  <span className="text-gray-700">{h.name}</span>
                  <span className="font-bold text-bundle-green">{h.handicap?.toFixed(1)}</span>
                </div>
              ))}
            </div>
          )}
          <Button onClick={() => navigate('/dashboard')} className="w-full btn-bundle" data-testid="back-dashboard-btn">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav" data-testid="live-scorecard">
      <Navigation user={user} />

      <div className="max-w-2xl mx-auto px-2 pt-16 md:pt-20">
        {/* Header */}
        <div className="flex items-center justify-between px-2 mb-3">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 text-bundle-green text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-center">
            <p className="font-heading font-bold text-sm text-bundle-green">{round.course_name}</p>
            <p className="text-[10px] text-gray-400">{round.tee_box} tees | {numHoles} holes | {isCompleted ? 'Completed' : 'In Progress'}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {!isCompleted && (
              <button onClick={() => setShowScanner(true)} className="p-1.5 rounded-lg bg-purple-50 text-purple-600" data-testid="scan-card-btn">
                <Camera className="w-4 h-4" />
              </button>
            )}
            {saving && <Loader2 className="w-4 h-4 animate-spin text-bundle-green" />}
          </div>
        </div>

        {/* Players Legend */}
        {players.length > 1 && (
          <div className="flex gap-2 px-2 mb-3 overflow-x-auto" data-testid="players-legend">
            {players.map((p, i) => {
              const t = calcTotals(i);
              const svp = t.total - parTotal;
              return (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-bundle-cream-dark shadow-sm text-xs flex-shrink-0">
                  <div className="w-6 h-6 rounded-full bg-bundle-green flex items-center justify-center text-white text-[10px] font-bold">
                    {p.name?.[0] || '?'}
                  </div>
                  <span className="font-bold text-bundle-green">{p.name?.split(' ')[0]}</span>
                  <span className="text-gray-500">{t.total || '-'}</span>
                  {t.total > 0 && <span className={`text-[10px] font-bold ${svp < 0 ? 'text-red-500' : svp === 0 ? 'text-gray-400' : 'text-gray-500'}`}>({svp > 0 ? '+' : ''}{svp})</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Color Legend */}
        <div className="flex items-center gap-2 px-2 mb-3 text-[9px]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-yellow-400" /> Eagle</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> Birdie</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-bundle-green" /> Par</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-100 border border-blue-200" /> Bogey</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-200" /> Dbl+</span>
          <span className="ml-auto text-gray-400">Tap cell to edit</span>
        </div>

        {/* Front 9 */}
        {renderNine(0, Math.min(9, numHoles), 'OUT')}

        {/* Back 9 */}
        {numHoles > 9 && renderNine(9, numHoles, 'IN')}

        {/* Totals Row */}
        <div className="overflow-x-auto mb-4">
          <table className="w-full border-collapse text-center text-xs">
            <thead>
              <tr className="bg-bundle-green-dark text-white">
                <th className="px-2 py-2 text-left font-bold text-[11px] sticky left-0 bg-bundle-green-dark z-10 min-w-[70px]">Total</th>
                <th className="px-3 py-2 font-bold">OUT</th>
                {numHoles > 9 && <th className="px-3 py-2 font-bold">IN</th>}
                <th className="px-3 py-2 font-bold bg-bundle-gold text-bundle-green-dark">TOTAL</th>
                <th className="px-3 py-2 font-bold">+/-</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-bundle-cream-dark text-gray-600">
                <td className="px-2 py-1.5 text-left font-bold sticky left-0 bg-bundle-cream-dark z-10">Par</td>
                <td className="px-3 py-1.5 font-medium">{parOut}</td>
                {numHoles > 9 && <td className="px-3 py-1.5 font-medium">{parIn}</td>}
                <td className="px-3 py-1.5 font-bold">{parTotal}</td>
                <td className="px-3 py-1.5">-</td>
              </tr>
              {players.map((player, pIdx) => {
                const t = calcTotals(pIdx);
                const svp = t.total - parTotal;
                return (
                  <tr key={pIdx} className={`border-b border-bundle-cream-dark ${pIdx % 2 === 0 ? 'bg-white' : 'bg-bundle-cream/50'}`}>
                    <td className="px-2 py-2 text-left font-bold text-bundle-green text-[11px] sticky left-0 z-10 truncate"
                      style={{ backgroundColor: pIdx % 2 === 0 ? 'white' : '#f5f0e8' }}>
                      {player.name?.split(' ')[0] || `P${pIdx + 1}`}
                    </td>
                    <td className="px-3 py-2 font-bold">{t.out || '-'}</td>
                    {numHoles > 9 && <td className="px-3 py-2 font-bold">{t.inn || '-'}</td>}
                    <td className="px-3 py-2 font-black text-lg text-bundle-green bg-bundle-gold/10">{t.total || '-'}</td>
                    <td className={`px-3 py-2 font-bold ${svp < 0 ? 'text-red-500' : svp === 0 ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t.total > 0 ? (svp > 0 ? '+' + svp : svp === 0 ? 'E' : svp) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        {isOwner && !isCompleted && (
          <div className="flex gap-2 px-2 mb-4">
            <Button variant="ghost" size="sm" onClick={abandonRound} className="text-red-500 hover:text-red-700 text-xs" data-testid="abandon-round-btn">
              <X className="w-3 h-3 mr-1" /> Abandon
            </Button>
            <div className="flex-1" />
            <Button onClick={completeRound} disabled={completing} className="btn-bundle text-xs" data-testid="complete-round-btn">
              {completing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
              Complete Round
            </Button>
          </div>
        )}

        {/* Post to Feed CTA — shown after round is completed */}
        {isOwner && isCompleted && (
          <div className="px-2 mb-4">
            <button
              onClick={() => {
                // Build scanData shape that PostRoundModal expects
                const playersForModal = players.map(p => {
                  const scores = { total: 0 };
                  const pars = {};
                  let totalScore = 0;
                  for (let h = 0; h < numHoles; h++) {
                    const s = p.holes?.[h]?.score;
                    if (s != null) { scores[String(h + 1)] = s; totalScore += s; }
                    pars[String(h + 1)] = round?.holes?.[h]?.par || 4;
                  }
                  scores.total = totalScore || p.total_score || 0;
                  return {
                    name: p.name,
                    member_id: p.user_id || null,
                    scores,
                    pars,
                  };
                });
                navigate('/feed', {
                  state: {
                    scanData: {
                      course_name: round.course_name,
                      date: (round.completed_at || round.started_at || new Date().toISOString()).split('T')[0],
                      players: playersForModal,
                      total_par: parTotal,
                      live_round_id: round.live_round_id,
                    },
                  },
                });
              }}
              data-testid="post-completed-round-btn"
              className="w-full py-3 bg-gradient-to-r from-bundle-green to-emerald-600 hover:from-bundle-green-dark hover:to-emerald-700 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2"
            >
              <Trophy className="w-5 h-5" /> Post to Feed (Bundle / Skins / Recap)
            </button>
            <p className="text-[10px] text-gray-400 text-center mt-1.5">Add bundle pots, skin value, and post the recap to the community feed</p>
          </div>
        )}
      </div>

      {/* Scorecard Scanner */}
      {showScanner && (
        <ScorecardScanner onClose={() => setShowScanner(false)} onScanComplete={() => {
          setShowScanner(false);
          toast.success('Scorecard scanned!');
        }} />
      )}
    </div>
  );
}
